<script lang="ts" setup>
import { Button } from '@forgd/ui-kit/button'
import { Separator } from '@forgd/ui-kit/separator'
import { ArrowUpRightIcon, ChevronDownIcon, ChevronUpIcon, NewspaperIcon } from '@heroicons/vue/24/outline'
import { buildArticleGroupSlices } from '../utils/article-groups'
import { fetchAcademyNav } from '../utils/nav'

interface MobileMenuArticle {
  title: string
  path: string
}

interface MobileMenuArticleGroup {
  top?: string
  sub?: string
  articles: MobileMenuArticle[]
}

interface MobileMenuCurrentChapter {
  title: string
  count: number
  path: string
  articles: MobileMenuArticle[]
  groups: MobileMenuArticleGroup[]
}

useHead({
  htmlAttrs: {
    class: 'scroll-smooth',
  },
})

const route = useRoute()
const academyNav = await fetchAcademyNav()

function navPageFromPath(path: string, navData: any[]): any {
  const item = navData.find(item => item.path === path)
  if (!item) {
    for (const child of navData) {
      if (child.children) {
        const found = navPageFromPath(path, child.children)
        if (found) {
          return found
        }
      }
    }
  }
  return item
}

const section = computed(() => {
  return academyNav.data.value?.find((item: { path: string }) => item.path === '/academy/lessons')
})

const collection = computed<any>(() => {
  const navData = academyNav.data.value
  if (!navData?.length) {
    return undefined
  }
  const segments = route.path.split('/')
  return navPageFromPath(segments.slice(0, -1).join('/'), navData)
})

function firstChildToIfDir(item: any) {
  return item.children?.[0]?.path ?? item.path
}

function isLessonActive(item: any) {
  return item.path === collection.value?.path || firstChildToIfDir(item) === route.path
}

const overviewPath = '/academy'

const isOverviewActive = computed(() => {
  return route.path === '/academy/overview' || route.path === '/academy'
})

const otherReadings = [
  { label: 'What is Forgd?', to: '/about/what-is-forgd' },
  { label: 'About Forgd FAQs', to: '/about/faqs' },
  { label: 'Use Cases: Blockchain Projects', to: '/about/blockchain-projects' },
  { label: 'Use Cases: Market Makers', to: '/about/market-makers' },
  { label: 'Use Cases: Blockchain Advisors', to: '/about/blockchain-advisors' },
]

const isMobileMenuOpen = ref(false)

const mobileMenuChapters = computed(() => {
  return section.value?.children?.map(child => ({
    title: child.title,
    count: child.children?.length ?? 0,
    to: firstChildToIfDir(child),
    path: child.path,
  })) ?? []
})

const mobileMenuCurrentChapter = computed<MobileMenuCurrentChapter | undefined>(() => {
  if (!collection.value || collection.value.path === section.value?.path) {
    return undefined
  }
  const articles: MobileMenuArticle[] = (collection.value.children ?? []).map((child: MobileMenuArticle) => ({
    title: child.title,
    path: child.path,
  }))
  const slices = buildArticleGroupSlices(collection.value.groups, articles)
  const groups: MobileMenuArticleGroup[] = slices.map(slice => ({
    top: slice.top,
    sub: slice.sub,
    articles: slice.articles,
  }))
  return {
    title: collection.value.title,
    count: collection.value.children?.length ?? 0,
    path: collection.value.path,
    articles,
    groups,
  }
})

const mobileTriggerLabel = computed(() => {
  if (isOverviewActive.value) {
    return 'Overview'
  }
  const currentArticle = mobileMenuCurrentChapter.value?.articles.find(a => a.path === route.path)
  return currentArticle?.title ?? mobileMenuCurrentChapter.value?.title ?? 'Overview'
})

const activeLessonTitle = computed(() => {
  const activeSection = section.value?.children?.find(child => isLessonActive(child))
  if (!activeSection) {
    return ''
  }
  const activeLesson = activeSection?.children?.find(child => isLessonActive(child)) as { title: string }
  if (!activeLesson) {
    return activeSection?.title
  }
  return `${activeSection?.title}: ${activeLesson?.title}`
})
</script>

<template>
  <div data-layout="academy-shell">
    <NuxtLayout
      name="public"
      title="Forgd Academy"
      icon="/lessons-and-faqs-icon-colored.svg"
      :seo="{
        title: 'Forgd Academy | Forgd',
        description: activeLessonTitle,
      }"
    >
      <template #sidebar>
        <nav
          aria-label="Academy navigation"
          class="hidden md:block w-sidebar shrink-0 sticky top-0 h-screen overflow-y-auto bg-neutral-200 border-r border-r-default"
        >
          <div class="text-primal font-display flex flex-col shrink-0">
            <div class="sticky bg-neutral-200 top-0 z-10">
              <div class="py-4 px-2">
                <Button
                  as-child
                  variant="ghost"
                  :class="{ 'bg-neutral-600/30': isOverviewActive }"
                  class="px-2"
                  block
                >
                  <NuxtLink :to="overviewPath" :aria-current="isOverviewActive ? 'page' : undefined">
                    <div class="text-xs text-secondary text-left font-normal w-full">
                      Overview
                    </div>
                  </NuxtLink>
                </Button>
              </div>
              <Separator />
              <div class="px-2 py-4 flex flex-col gap-1">
                <Button
                  v-for="child in section?.children"
                  :key="child.title"
                  as-child
                  variant="ghost"
                  :class="{ 'bg-neutral-600/30': isLessonActive(child) }"
                  class="px-2"
                >
                  <NuxtLink :to="firstChildToIfDir(child)" :aria-current="isLessonActive(child) ? 'page' : undefined">
                    <span class="text-xs text-secondary text-left font-normal w-full">
                      {{ child.title }}
                      <span class="text-neutral-900/30">({{ child.children?.length }})</span>
                    </span>
                  </NuxtLink>
                </Button>
              </div>
            </div>
          </div>
          <Separator />
          <div class="px-2 py-4 flex flex-col gap-1">
            <div class="pt-3 pb-4 flex items-center gap-2">
              <NewspaperIcon class="size-5" aria-hidden="true" />
              <span class="font-semibold text-xs">
                Other Readings
              </span>
            </div>
            <Button
              v-for="reading in otherReadings"
              :key="reading.to"
              as-child
              variant="ghost"
              class="px-2"
            >
              <NuxtLink :to="reading.to" target="_blank">
                <span class="text-xs text-secondary text-left font-normal w-full">
                  {{ reading.label }}
                </span>
                <ArrowUpRightIcon class="size-3 text-secondary" aria-hidden="true" />
              </NuxtLink>
            </Button>
          </div>
        </nav>
      </template>

      <template #subheader>
        <button
          type="button"
          class="md:hidden flex items-center justify-between w-full h-11 px-4 border-b border-default bg-page relative z-50"
          @click="isMobileMenuOpen = !isMobileMenuOpen"
        >
          <span class="text-xs text-secondary truncate">{{ mobileTriggerLabel }}</span>
          <component
            :is="isMobileMenuOpen ? ChevronUpIcon : ChevronDownIcon"
            class="size-5 text-secondary shrink-0 ml-2"
            aria-hidden="true"
          />
        </button>
        <AcademyMobileOverviewMenu
          v-model:open="isMobileMenuOpen"
          :chapters="mobileMenuChapters"
          :current-chapter="mobileMenuCurrentChapter"
          :current-path="route.path"
          :overview-path="overviewPath"
        />
      </template>

      <slot />

      <template #app-footer>
        <CoreAppFooterBlock />
      </template>
    </NuxtLayout>
  </div>
</template>

<style scoped>
/*
 * Academy does not render through the same layout chain in every app.
 * In the main app it goes through the public layout directly, but in liquidity
 * the public layout is wrapped by dashboard -> core-dashboard. In that shell,
 * the flex item for <main> keeps min-width:auto by default. We need this CSS
 * because long academy content, especially the prev/next article navigation,
 * can otherwise force the page to overflow horizontally on mobile and other
 * smaller viewports. Scope this to academy-shell so we only opt academy pages into the
 * shrink behavior without changing other dashboard pages.
 */
[data-layout='academy-shell'] :deep([data-layout='core-dashboard'] main[data-inertable]){
  min-width: 0;
}
</style>
